import { Result, Button } from "antd";
import { auth } from "~/lib/firebase";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
  useRouteError,
} from "react-router-dom";
import { apiClient } from "~/services/apiClient";
import {
  _AmenityPageTabType,
  GETDBFileObj,
  LoaderData,
  PrecomputedAmenityData,
  ReservationListFiltersType,
} from "~/types";
import { INTENTS } from "~/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import * as APITypes from "~/API";

dayjs.extend(utc);

/** Transform Lambda response (ISO strings) to PrecomputedAmenityData (dayjs) */
function transformLambdaAmenityData(raw: {
  formattedAmenityDetails: {
    amenityDetails: APITypes.Amenity;
    propertyDetails: APITypes.Property;
    buildingDetails: APITypes.Building;
    reservations: Array<{
      timeFrame: { start: string; end: string };
      createdAt: string;
      data: APITypes.AmenityReservation;
    }>;
  };
  availability: {
    available: Array<Array<{ start: string; end: string }>>;
    unavailable: Array<{ timeFrame: { start: string; end: string }; reason?: string }>;
    fullyUnavailable: string | boolean;
  };
}): PrecomputedAmenityData {
  return {
    formattedAmenityDetails: {
      ...raw.formattedAmenityDetails,
      reservations: raw.formattedAmenityDetails.reservations.map((r) => ({
        timeFrame: {
          start: dayjs(r.timeFrame.start),
          end: dayjs(r.timeFrame.end),
        },
        createdAt: dayjs(r.createdAt),
        data: r.data,
      })),
    },
    availability: {
      available: (() => {
        const days = raw.availability.available.slice(0, 7);
        while (days.length < 7) days.push([]);
        const result = days.map((day) =>
          day.map((tf) => ({
            start: dayjs(tf.start),
            end: dayjs(tf.end),
          }))
        );
        return result as PrecomputedAmenityData["availability"]["available"];
      })(),
      unavailable: raw.availability.unavailable.map((u) => ({
        timeFrame: {
          start: dayjs(u.timeFrame.start),
          end: dayjs(u.timeFrame.end),
        },
        reason: u.reason,
      })),
      fullyUnavailable: raw.availability.fullyUnavailable,
    },
  };
}

export async function AmenityDetailsPageLoader({
  request,
  params,
}: LoaderFunctionArgs): Promise<AmenityDetailsPageLoaderType | Response> {
  const user = auth.currentUser;
  if (!user) return redirect("/");

  const amenityID = params.amenityID || "";
  if (amenityID === "") return redirect("/amenity");

  const { searchParams } = new URL(request.url);
  const tab = (searchParams.get("tab") ?? "details") as _AmenityPageTabType;

  if (tab !== "reservations") {
    searchParams.delete("status");
  }

  const reservationListFilters: ReservationListFiltersType = {
    status:
      (searchParams.get("status") as APITypes.AmenityReservationStatus) ||
      undefined,
  };

  const result = await apiClient.loader<{
    amenityData: Parameters<typeof transformLambdaAmenityData>[0];
    buildingName: string;
    files: Array<{ Key?: string; url: string }>;
  }>("amenity-details", { id: amenityID });

  if (!result.success || !result.data) {
    console.error("Amenity details loader failed:", result.error);
    throw new Response(result.error ?? "Failed to load amenity", { status: 500 });
  }

  const amenityData = transformLambdaAmenityData(result.data.amenityData);

  const fileList: LoaderData<GETDBFileObj[]> = {
    success: true,
    data: (result.data.files ?? []).map((f) => ({
      id: f.Key ?? "",
      value: f.url,
      url: f.url,
    })) as GETDBFileObj[],
  };

  return {
    amenityData,
    buildingName: result.data.buildingName,
    fileList,
    userID: user.uid,
    tab,
    filters: reservationListFilters,
  };
}

export type AmenityDetailsPageLoaderType = {
  amenityData: PrecomputedAmenityData;
  buildingName: string;
  fileList: LoaderData<GETDBFileObj[]>;
  userID: string;
  tab: _AmenityPageTabType;
  filters: ReservationListFiltersType;
};

export const AmenityDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  switch (intent) {
    case INTENTS._edit_amenity_reservations_cancel: {
      const payload = JSON.parse(formData.get("body") as string);
      const reservationIDs = payload.reservationIDs as string[];

      const result = await apiClient.sendIntent<APITypes.AmenityReservation[]>(
        INTENTS._edit_amenity_reservations_cancel,
        { reservationIDs }
      );

      if (!result.success || !result.data) {
        console.error("Error canceling reservations:", result.error);
        throw new Response(result.error ?? "Failed to cancel reservations", {
          status: 500,
        });
      }

      return result.data;
    }
  }
  return null;
};

export const AmenitiesDetailsPageErrorBoundary = () => {
  const error = useRouteError();
  console.error("Amenities details page error", error);
  return (
    <Result status="500" title="500" subTitle="Sorry, something went wrong.">
      <Button type="primary" href="/">
        Back Home
      </Button>
    </Result>
  );
};
